import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faStore,
  faBuilding,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";
import { shopService } from "../../../services/service";

const FALLBACK_TYPES = [
  "Retail",
  "Wholesale",
  "Service",
  "Manufacturing",
  "Distribution",
  "E-commerce",
  "Hospitality",
  "Healthcare",
  "Education",
  "Technology",
  "Construction",
  "Agriculture",
  "Transportation",
  "Real Estate",
  "Financial",
  "Other",
];

const AddShop = ({ onAdd, onClose }) => {
  const [form, setForm] = useState({
    shop_name: "",
    location: "",
    shop_type: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shopTypes, setShopTypes] = useState(FALLBACK_TYPES);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await shopService.getTypes();
        const raw = response?.data?.data ?? response?.data ?? [];
        if (mounted && Array.isArray(raw) && raw.length) {
          setShopTypes(raw);
        }
      } catch {
        if (mounted) setShopTypes(FALLBACK_TYPES);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = () => {
    const errors = {};

    if (!form.shop_name.trim()) {
      errors.shop_name = "Shop name is required";
    }
    if (!form.location.trim()) {
      errors.location = "Location is required";
    }
    if (!form.shop_type) {
      errors.shop_type = "Shop type is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onAdd({
        shop_name: form.shop_name,
        location: form.location,
        shop_type: form.shop_type,
        status: "active",
      });

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess(`Shop "${form.shop_name}" added successfully!`);
            setIsSubmitting(false);
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to add shop: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess(`Shop "${form.shop_name}" added successfully!`);
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      toastError(`Failed to add shop: ${error.message || "Please try again."}`);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add new shop"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add shop
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faStore} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">Add new shop</p>
            <p className="text-xs text-gray-600 font-medium">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faStore}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop Name <span className="text-red-700">*</span>
          </Label>
          <Input
            name="shop_name"
            value={form.shop_name}
            onChange={handleChange}
            placeholder="Enter Shop name"
            className={`h-10 text-sm ${formErrors.shop_name ? "border-red-500" : "border-gray-300"}`}
          />
          {formErrors.shop_name && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.shop_name}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBuilding}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Location <span className="text-red-700">*</span>
          </Label>
          <Input
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder="Enter shop location"
            className={`h-10 text-sm ${formErrors.location ? "border-red-500" : "border-gray-300"}`}
          />
          {formErrors.location && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.location}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTag}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop Type <span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.shop_type}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, shop_type: value }));
              if (formErrors.shop_type) {
                setFormErrors((prev) => ({ ...prev, shop_type: "" }));
              }
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${formErrors.shop_type ? "border-red-500" : "border-gray-300"}`}
            >
              <SelectValue placeholder="Select Shop type" />
            </SelectTrigger>
            <SelectContent>
              {shopTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.shop_type && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.shop_type}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddShop;
